import React, { useState } from 'react';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { STATE_LABEL, canRetry, cancelRemaining, clearFinished, isPrinting, isQueueActive, resolveHalt, sheetsOf } from './printQueue.js';

const WAITING = new Set(['uploading', 'queued', 'sending', 'pending', 'pending-held']);
const NOT_PRINTED = new Set(['failed', 'canceled', 'skipped']);
const TONE = {
  processing: 'text-[#1F4E9C]',
  'processing-stopped': 'text-[#A35F00]',
  'pending-held': 'text-[#A35F00]',
  completed: 'text-[#1E7B45]',
  canceled: 'text-[#8A94A6]',
  skipped: 'text-[#8A94A6]',
  failed: 'text-[#9B1C1C]',
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const sheetsDoneOf = (j) => {
  if (j.state === 'completed') return sheetsOf(j);
  return isPrinting(j) ? Math.min(sheetsOf(j), j.sheetsDone || 0) : 0;
};

// "14 of 40 sheets, copy 2 of 3" for the job on the glass right now.
const progressText = (j) => {
  const total = sheetsOf(j);
  if (j.sheetsDone == null) return total ? plural(total, 'sheet') : 'starting';
  const done = Math.min(j.sheetsDone, total || j.sheetsDone);
  let text = total ? `${done} of ${total} sheets` : `${plural(done, 'sheet')} out`;
  if (j.copies > 1 && j.sheetsPerCopy) text += `, copy ${Math.min(j.copies, Math.floor(done / j.sheetsPerCopy) + 1)} of ${j.copies}`;
  return text;
};

const LinkButton = ({ children, tone = 'text-[#1F4E9C]', ...props }) => (
  <button type="button" className={`whitespace-nowrap text-[13px] font-semibold hover:underline disabled:opacity-50 ${tone}`} {...props}>{children}</button>
);

function HaltBanner({ batch, busy }) {
  const job = batch.jobs[batch.halt.index];
  return (
    <div role="alert" className="mt-2 rounded-md border border-[#E7B4B4] bg-[#FDECEC] px-3 py-2 text-[13px] text-[#7A1A1A]">
      <p>
        <span className="font-semibold">Paused: {job.name} didn&apos;t print.</span> {batch.halt.reason}.
        {' '}Nothing after it will print until you choose what to do. An email with the details has gone out.
      </p>
      <div className="mt-1.5 flex flex-wrap gap-4">
        <LinkButton disabled={busy || !canRetry(batch.id, job)} title={canRetry(batch.id, job) ? undefined : 'This browser no longer has the file'} onClick={() => resolveHalt(batch.id, 'retry')}>Print it again</LinkButton>
        <LinkButton disabled={busy} onClick={() => resolveHalt(batch.id, 'printed')}>It did print, carry on</LinkButton>
        <LinkButton disabled={busy} tone="text-[#9B1C1C]" onClick={() => resolveHalt(batch.id, 'skip')}>Skip it, carry on</LinkButton>
      </div>
    </div>
  );
}

export default function PrintQueuePanel({ queue }) {
  const [showJobs, setShowJobs] = useState(false);
  const { batches, jobs, busy, error } = queue;
  const active = isQueueActive(queue);
  const halted = batches.filter((b) => b.status === 'halted' && b.halt);
  const current = jobs.find(isPrinting);
  const currentBatch = current && batches.find((b) => b.id === current.batchId);
  const next = jobs.find((j) => WAITING.has(j.state));
  const nextBatch = next && batches.find((b) => b.id === next.batchId);
  const remaining = jobs.filter((j) => WAITING.has(j.state)).length;
  const counted = jobs.filter((j) => !NOT_PRINTED.has(j.state));
  const total = counted.reduce((sum, j) => sum + sheetsOf(j), 0);
  const done = counted.reduce((sum, j) => sum + sheetsDoneOf(j), 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const count = (state) => jobs.filter((j) => j.state === state).length;
  const printed = count('completed');
  const canceled = count('canceled') + count('skipped');
  const failed = count('failed');
  const printers = [...new Set(jobs.map((j) => j.printerName).filter(Boolean))].join(', ');
  const notes = [...new Set(batches.map((b) => b.note).filter(Boolean))];

  let now = null;
  if (current) {
    const paused = current.state === 'processing-stopped';
    const why = current.message || (current.reasons || []).join(', ');
    now = (
      <>
        <span className={`font-semibold ${paused ? 'text-[#A35F00]' : ''}`}>{paused ? 'Paused on' : 'Printing'}</span>{' '}
        {current.name}
        <span className="text-[#5C6B7A]">, {paused && why ? `${why}. Check the printer.` : progressText(current)}</span>
      </>
    );
  } else if (halted.length) {
    now = 'Paused until you choose what to do below.';
  } else if (next) {
    const behind = nextBatch?.waitingBehind;
    if (next.state === 'uploading') now = `Uploading ${next.name}…`;
    else if (next.state === 'pending-held') now = `${next.name} is held at the printer. Release it from the printer's panel.`;
    else if (next.state === 'pending') now = `Queued at the printer${next.ahead ? `, ${plural(next.ahead, 'other job')} ahead` : ''}.`;
    else if (behind) now = `Waiting for ${behind.requestedBy || 'another'}'s print to finish first (${plural(behind.left, 'document')} left${behind.halted ? ', paused on an error' : ''}).`;
    else now = `Sending ${next.name}…`;
  } else if (jobs.length) {
    now = failed || canceled ? `Finished. ${plural(failed + canceled, 'document')} didn't print; they're marked below.` : 'Everything printed.';
  }

  const tally = [
    `${printed} of ${plural(jobs.length, 'document')} printed`,
    canceled && `${canceled} canceled or skipped`,
    failed && `${failed} didn't print`,
  ].filter(Boolean).join(', ');

  return (
    <section aria-label="Print queue" className="shrink-0 border-t border-[#D5DCE3] bg-[#F7F9FB] px-5 py-3 text-sm">
      <div className="flex items-center gap-3">
        <h2 className="text-[15px] font-bold">Print queue</h2>
        {printers && <span className="min-w-0 truncate text-[#5C6B7A]">{printers}</span>}
        <span className="ml-auto inline-flex items-center gap-2">
          {jobs.length > 0 && !active && (
            <Button size="small" onClick={() => clearFinished()} className="min-w-0 font-semibold normal-case text-[#1F4E9C]">Close</Button>
          )}
          <Button
            variant="outlined"
            size="small"
            disabled={!active || Boolean(busy) || batches.every((b) => b.status === 'finished' || b.cancelRequested)}
            onClick={() => cancelRemaining()}
            className="rounded-md border-[#E7B4B4] bg-white font-semibold normal-case text-[#9B1C1C] hover:bg-[#FDECEC]"
          >
            {busy ? 'Working…' : `Cancel remaining${remaining ? ` (${remaining})` : ''}`}
          </Button>
        </span>
      </div>

      {!jobs.length ? (
        <p className="mt-1 text-[#5C6B7A]">Getting the documents ready to send…</p>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-3">
            <LinearProgress
              variant="determinate"
              value={pct}
              aria-label="Sheets printed"
              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#DFE4EC', '& .MuiLinearProgress-bar': { bgcolor: '#1F4E9C', borderRadius: 3 } }}
            />
            <span className="whitespace-nowrap font-semibold">{done} of {plural(total, 'sheet')}</span>
          </div>

          <div className="mt-1.5 flex items-center gap-3" aria-live="polite">
            <span className="min-w-0 flex-1 truncate">{now}</span>
            {current && currentBatch?.cancelScope === 'rest' && (
              <LinkButton tone="text-[#9B1C1C]" disabled={Boolean(busy)} onClick={() => cancelRemaining({ includeCurrent: true })}>Stop this one too</LinkButton>
            )}
          </div>

          {halted.map((b) => <HaltBanner key={b.id} batch={b} busy={Boolean(busy)} />)}

          <div className="mt-1 flex items-center gap-3 text-[13px] text-[#5C6B7A]">
            <span>{tally}</span>
            <LinkButton onClick={() => setShowJobs((v) => !v)} aria-expanded={showJobs}>{showJobs ? 'Hide jobs' : 'Show jobs'}</LinkButton>
          </div>

          {showJobs && (
            <ol className="mt-1.5 max-h-40 overflow-auto rounded-md border border-[#D5DCE3] bg-white">
              {jobs.map((j, i) => (
                <li key={j.key} className="grid grid-cols-[28px_minmax(0,1fr)_auto_120px] items-center gap-3 border-b border-[#EEF1F4] px-3 py-1 text-[13px] last:border-0">
                  <span className="text-[#5C6B7A]">{i + 1}</span>
                  <span className="truncate" title={j.error || j.name}>{j.name}</span>
                  <span className="whitespace-nowrap text-[#5C6B7A]">{sheetsOf(j) ? `${sheetsDoneOf(j)}/${sheetsOf(j)}` : ''}</span>
                  <span className={`truncate text-right font-semibold ${TONE[j.state] || 'text-[#4A5465]'}`} title={j.error || undefined}>
                    {STATE_LABEL[j.state] || j.state}
                  </span>
                </li>
              ))}
            </ol>
          )}

          {[error, ...notes].filter(Boolean).map((text) => <p key={text} className="mt-1.5 text-[13px] text-[#A35F00]">{text}</p>)}
          {active && !jobs.some((j) => j.state === 'uploading') && (
            <p className="mt-1.5 text-[13px] text-[#5C6B7A]">Everything is on the print server, so you can close this window; it will keep printing in order.</p>
          )}
        </>
      )}
    </section>
  );
}
